/**
 * ============================================================================
 * IN-APP NOTIFICATION LISTENER
 * ============================================================================
 * Shows push notifications as in-app toasts when the app is in foreground
 * Integrates with your existing toast/notification system
 * 
 * Usage: Add <InAppNotificationListener /> to your App.tsx near the root
 * 
 * Location: web-app/src/components/InAppNotificationListener.tsx
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  IconButton,
  Slide,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { POKER_COLORS } from '../theme/designSystem';
import { PushNotificationData } from '../services/pushNotificationService';

// ============================================================================
// TYPES
// ============================================================================

interface NotificationToastData {
  id: string;
  title: string;
  body: string;
  data?: PushNotificationData;
  timestamp: number;
}

// ============================================================================
// NOTIFICATION TOAST COMPONENT
// ============================================================================

interface NotificationToastProps {
  notification: NotificationToastData;
  onDismiss: () => void;
  onTap: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onDismiss,
  onTap,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Slide in
    setIsVisible(true);
    
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300); // Wait for animation
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const getIcon = () => {
    switch (notification.data?.type) {
      case 'announcement': return '';

      case 'mention': return '';

      case 'dm': return '';

      case 'table_invite': return '';

      case 'game_alert': return '🃏';
      case 'promotion': return '';

      default: return '';

    }
  };

  const getColor = () => {
    switch (notification.data?.type) {
      case 'announcement': return 'cyan.600';
      case 'mention': return 'purple.600';
      case 'dm': return 'blue.600';
      case 'table_invite': return 'orange.600';
      case 'game_alert': return 'green.600';
      case 'promotion': return 'yellow.600';
      default: return 'cyan.600';
    }
  };

  return (
    <Slide direction="top" in={isVisible} style={{ zIndex: 9999 }}>
      <Box
        position="fixed"
        top="env(safe-area-inset-top, 10px)"
        left="50%"
        transform="translateX(-50%)"
        width="calc(100% - 24px)"
        maxW="400px"
        mt={2}
      >
        <Box
          bg={POKER_COLORS.cardBg || 'gray.800'}
          borderRadius="xl"
          border="1px solid"
          borderColor={getColor()}
          boxShadow={`0 8px 32px rgba(0, 217, 217, 0.2)`}
          overflow="hidden"
          cursor="pointer"
          onClick={onTap}
          _hover={{ transform: 'scale(1.01)' }}
          _active={{ transform: 'scale(0.99)' }}
          transition="transform 0.2s"
        >
          <HStack p={3} spacing={3} align="start">
            {/* Icon */}
            <Box
              bg={POKER_COLORS.darkBg || 'gray.700'}
              borderRadius="lg"
              p={2}
              fontSize="24px"
              flexShrink={0}
            >
              {getIcon()}
            </Box>

            {/* Content */}
            <VStack align="start" spacing={0} flex={1} minW={0}>
              <Text 
                color="white" 
                fontWeight="bold" 
                fontSize="sm"
                noOfLines={1}
              >
                {notification.title}
              </Text>
              <Text 
                color="gray.400" 
                fontSize="xs"
                noOfLines={2}
              >
                {notification.body}
              </Text>
            </VStack>

            {/* Dismiss Button */}
            <IconButton
              aria-label="Dismiss"
              icon={<Text fontSize="lg"></Text>}

              size="sm"
              variant="ghost"
              color="gray.500"
              _hover={{ color: 'white', bg: 'gray.700' }}
              onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
                setTimeout(onDismiss, 300);
              }}
            />
          </HStack>

          {/* Progress bar */}
          <Box
            h="3px"
            bg={getColor()}
            animation="shrink 5s linear forwards"
            sx={{
              '@keyframes shrink': {
                from: { width: '100%' },
                to: { width: '0%' },
              },
            }}
          />
        </Box>
      </Box>
    </Slide>
  );
};

// ============================================================================
// NOTIFICATION LISTENER COMPONENT
// ============================================================================

const InAppNotificationListener: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationToastData[]>([]);

  // Handle incoming push notifications
  useEffect(() => {
    const handlePushNotification = (event: CustomEvent) => {
      const { title, body, data, id } = event.detail;
      
      console.log('📱 [IN-APP] Received notification:', title);
      
      // Add to queue
      const notification: NotificationToastData = {
        id: id || `notif-${Date.now()}`,
        title,
        body,
        data,
        timestamp: Date.now(),
      };
      
      setNotifications(prev => [...prev, notification]);
    };

    // Listen for push notifications from pushNotificationService
    window.addEventListener('pushNotificationReceived', handlePushNotification as EventListener);
    
    return () => {
      window.removeEventListener('pushNotificationReceived', handlePushNotification as EventListener);
    };
  }, []);

  // Handle notification tap
  const handleTap = useCallback((notification: NotificationToastData) => {
    const data = notification.data;
    
    // Remove notification
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Navigate based on type
    if (!data?.type) return;
    
    switch (data.type) {
      case 'table_invite':
        if (data.tableId) {
          navigate(data.inviteToken 
            ? `/game/${data.tableId}?invite=${data.inviteToken}`
            : `/game/${data.tableId}`
          );
        }
        break;

      case 'mention':
        if (data.feedItemId || data.storyId) {
          navigate(`/feed?highlight=${data.feedItemId || data.storyId}`);
        } else {
          navigate('/feed');
        }
        break;

      case 'dm':
        if (data.conversationId) {
          navigate(`/messages?conversation=${data.conversationId}`);
        } else if (data.fromUserId) {
          navigate(`/messages/${data.fromUserId}`);
        } else {
          navigate('/messages');
        }
        break;

      case 'announcement':
        // Stay on current page for announcements
        break;

      case 'game_alert':
        if (data.tableId) {
          navigate(`/game/${data.tableId}`);
        }
        break;

      case 'promotion':
        navigate(data.url || '/wallet');
        break;
    }
  }, [navigate]);

  // Handle dismiss
  const handleDismiss = useCallback((notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Only show the most recent notification
  const currentNotification = notifications[0];

  if (!currentNotification) return null;

  return (
    <NotificationToast
      key={currentNotification.id}
      notification={currentNotification}
      onDismiss={() => handleDismiss(currentNotification.id)}
      onTap={() => handleTap(currentNotification)}
    />
  );
};

export default InAppNotificationListener;